import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog.jsx';
import { Button } from '@/components/ui/button.jsx';
import { toast } from '@/components/ui/toaster.jsx';
import { cn } from '@/lib/utils.js';
import { formatCurrency } from '@/lib/format.js';
import { voidTransaction } from '@/services/deals.service.js';

// Void door for a ledger row (POST /transactions/:txId/void). Soft delete:
// the row stays in the history struck through, and the reason is stamped
// onto it for the audit trail, which is why the reason is required here and
// not merely suggested. The server refuses to void a row that non-voided
// children still reference (409 HAS_CHILDREN); that lands as guidance, not
// as a raw error.

const TYPE_LABEL = { bind: 'bind', endorsement: 'endorsement', cancellation: 'cancellation', correction: 'correction' };
// deferred included: it carries no cash but it carries David's DECISION,
// and voiding one silently discards it (2026-08-12). financed included for
// the same reason (190): voiding it discards the finance record and the
// carrier-paid mark. This is "money in motion or a decision on the row", a
// different fact from the settlement predicates in @/lib/arStatus.js.
const PAID = new Set(['pending', 'succeeded', 'deferred', 'financed']);

// Voiding a paid bind is legitimate (Sheffield N6395J, Starr-to-Beacon
// rebind) but consequential: the void does not touch the money.
function paymentOnRow(txn) {
  if (!txn) return null;
  const ar = String(txn.ar_payment_status || 'na').toLowerCase();
  if (!PAID.has(ar) && txn.ar_paid_amount == null) return null;
  return { status: ar, amount: txn.ar_paid_amount };
}

export default function VoidTransactionDialog({ open, onOpenChange, txn, onVoided }) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) setReason('');
  }, [open]);

  if (!txn) return null;
  const label = TYPE_LABEL[txn.type] || 'transaction';
  const payment = paymentOnRow(txn);

  async function handleVoid() {
    if (reason.trim().length < 3) return;
    setBusy(true);
    try {
      const res = await voidTransaction(txn.id, reason.trim());
      if (res?.success === false) throw new Error(res?.error || 'Void failed');
      toast.success(`Voided the ${label}. It stays in the history, struck through.`);
      onOpenChange?.(false);
      onVoided?.();
    } catch (err) {
      if (err?.body?.code === 'HAS_CHILDREN') {
        toast.error('Later transactions build on this one. Void those first, newest to oldest, then retry.');
      } else {
        toast.error(err?.body?.error || err.message || 'Void failed');
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Void this {label}?</DialogTitle>
          <DialogDescription>
            The row stays in the transaction history, struck through, with your
            reason on it. Derived totals stop counting it.
          </DialogDescription>
        </DialogHeader>

        {payment ? (
          <div className="rounded-card border border-warning/40 bg-warning/10 px-3 py-2 text-xs text-text">
            <span className="font-medium">This {label} carries a payment</span>
            {payment.amount != null ? <> of {formatCurrency(payment.amount)}</> : null}
            {PAID.has(payment.status) ? <> ({payment.status})</> : null}.
            Voiding the transaction does not refund or move any money. Handle the
            payment separately if it needs to follow.
          </div>
        ) : null}

        <label className="block">
          <span className="text-xs font-medium uppercase tracking-wide text-text-muted">
            Reason *
          </span>
          <textarea
            autoFocus
            required
            rows={2}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. rebound with a different carrier, entered against the wrong deal"
            maxLength={500}
            className={cn(
              'mt-1.5 flex w-full rounded-card border border-border bg-bg px-3 py-2 text-sm text-text',
              'placeholder:text-text-dim',
              'focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-1 focus:ring-offset-bg',
              'resize-none'
            )}
          />
        </label>

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => onOpenChange?.(false)} disabled={busy}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={handleVoid}
            disabled={busy || reason.trim().length < 3}
          >
            {busy ? 'Voiding…' : `Void ${label}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
